<template>
    <form @submit.prevent="submit">
        <div class="card shadow-sm border-0 overflow-hidden">
            <div class="card-header bg-white py-3 border-bottom-0 d-flex justify-content-between align-items-center">
                <h6 class="mb-0 fw-bold text-dark">Factory Employee Roster</h6>
                <span class="badge bg-light text-primary border px-3 py-2 rounded-pill">
                    Total: {{ totalEmployee }}
                </span>
            </div>

            <div class="card-body p-0">
                <div class="table-responsive">
                    <table class="table table-hover align-middle mb-0 custom-table">
                        <thead class="bg-light text-muted">
                            <tr>
                                <th class="ps-4 py-3 border-0 text-uppercase x-small fw-bold" style="width: 60px;">#</th>
                                <th class="py-3 border-0 text-uppercase x-small fw-bold">Employee Name</th>
                                <th class="py-3 border-0 text-uppercase x-small fw-bold">Designation</th>
                                <th class="py-3 border-0 text-uppercase x-small fw-bold">Contact Email</th>
                                <th class="py-3 border-0 text-uppercase x-small fw-bold text-center" style="width: 120px;">Actions</th>
                            </tr>
                        </thead>
                        <tbody class="border-top-0">
                        <tr v-for="(employee, index) in factoryEmployee" :key="index" class="employee-row">
                            <td class="ps-4 fw-medium text-muted">{{ index + 1 }}</td>
                            <td>
                                <div class="input-wrapper">
                                    <Input
                                        v-model="employee.name"
                                        :input-id="`name-${index}`"
                                        title=""
                                        :bootstrap="true"
                                        col="col-12"
                                        otherClass="m-0"
                                        placeholder="Full Name"
                                        @input="validateEmployee(index)"
                                        @customBlur="setValidateEmployees"
                                    />
                                </div>
                                <small class="text-danger d-block mt-1" v-if="errorsFactoryEmployee[index]?.name" style="font-size: 0.75rem;">
                                    <i class="fas fa-exclamation-circle me-1"></i> {{ errorsFactoryEmployee[index].name }}
                                </small>
                            </td>
                            <td>
                                <div class="input-wrapper">
                                    <Input
                                        v-model="employee.designation"
                                        :input-id="`desg-${index}`"
                                        title=""
                                        :bootstrap="true"
                                        col="col-12"
                                        otherClass="m-0 border-0"
                                        placeholder="e.g. Supervisor"
                                        @input="validateEmployee(index)"
                                        @customBlur="setValidateEmployees"
                                    />
                                </div>
                                <small class="text-danger d-block mt-1" v-if="errorsFactoryEmployee[index]?.designation" style="font-size: 0.75rem;">
                                    <i class="fas fa-exclamation-circle me-1"></i> {{ errorsFactoryEmployee[index].designation }}
                                </small>
                            </td>
                            <td>
                                <div class="input-wrapper">
                                    <Input
                                        v-model="employee.email"
                                        :input-id="`email-${index}`"
                                        title=""
                                        :bootstrap="true"
                                        col="col-12"
                                        otherClass="m-0 border-0"
                                        placeholder="email@company.com"
                                        @input="validateEmployee(index)"
                                        @customBlur="setValidateEmployees"
                                    />
                                </div>
                                <small class="text-danger d-block mt-1" v-if="errorsFactoryEmployee[index]?.email" style="font-size: 0.75rem;">
                                    <i class="fas fa-exclamation-circle me-1"></i> {{ errorsFactoryEmployee[index].email }}
                                </small>
                            </td>
                            <td class="text-center">
                                <div class="d-flex justify-content-center gap-1">
                                    <button
                                        v-if="factoryEmployee.length > 1"
                                        @click.prevent="removeRow(index, employee.id)"
                                        class="btn btn-action btn-outline-danger"
                                        title="Remove Row"
                                    >
                                        <i class="fa fa-trash-alt"></i>
                                    </button>
                                    <button
                                        v-if="index === factoryEmployee.length -1"
                                        @click.prevent="addRow"
                                        title="Add New Member"
                                        class="btn btn-action btn-outline-success"
                                    >
                                        <i class="fa fa-plus"></i>
                                    </button>
                                </div>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="card-footer bg-white border-top-0 py-4">
                <div class="d-flex align-items-center justify-content-between">
                    <p class="text-muted small mb-0">
                        <i class="fa fa-info-circle me-1"></i> Press the plus icon to add more rows.
                    </p>
                    <Button
                        :title="buttonText"
                        loaderClass="rs-btn rs-btn-primary px-5"
                        btnClass="rs-btn rs-btn-primary px-5 shadow-sm"
                        loadingText="Processing..."
                        :bootstrap="true"
                    />
                </div>
            </div>
        </div>
    </form>
</template>

<script setup>
import {computed, getCurrentInstance, onBeforeMount, ref} from "vue";
const { proxy } = getCurrentInstance()
const emptyRow = () => ({
    id: undefined,
    name: '',
    designation: '',
    email: '',
});

const errorsFactoryEmployee = ref([])
const factoryEmployee = ref([emptyRow()])
const totalEmployee = ref(0);

const buttonText = computed(() => (totalEmployee.value > 0 ? 'Update Employee Records' : 'Save Employee Records'));

const removeRow = async (index, id) => {
    if (!id) {
        factoryEmployee.value.splice(index, 1);
        return;
    }

    const success = await deleteItem(id);
    if (success &&  factoryEmployee.value.length > 1) {
        factoryEmployee.value.splice(index, 1);
    }
};
const addRow = () => factoryEmployee.value.push(emptyRow());
const deleteItem = async (id) => {
    const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, Delete it!'
    });

    if (result.isConfirmed) {
        try {
            const requestData = {id: id};
            const { data:res } = await axios.delete(route('web.client.employee.delete'), { data: requestData });
            $toast(res.message, res.type)
            await getFactoryEmployee();
            return true;
        } catch (error) {
            console.error('Error deleting item:', error);
            $toast(error.response?.data?.message ?? 'Deletion failed', 'error');
            return false;
        }

    }
}

const validateEmployee = (index) => {
    const row = factoryEmployee.value[index];
    const error = {};

    // Employee name validation
    if (!row.name || row.name.trim() === "") {
        error.name = "Employee name is required";
    }
    // Designation validation
    if (!row.designation || row.designation.trim() === "") {
        error.designation = "Designation is required";
    }

    // Email validation
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!row.email || row.email.trim() === "") {
        error.email = "Contact email is required";
    } else if (!emailPattern.test(row.email)) {
        error.email = "Invalid email format";
    }

    errorsFactoryEmployee.value[index] = error;
}
const setValidateEmployees = () => {
    errorsFactoryEmployee.value = [];

    factoryEmployee.value.forEach((item, index) => {
        const error = {};

        // Employee name validation
        if (!item.name || item.name.trim() === "") {
            error.name = "Employee name is required";
        }
        // Designation validation
        if (!item.designation || item.designation.trim() === "") {
            error.designation = "Designation is required";
        }

        // Email validation
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!item.email || item.email.trim() === "") {
            error.email = "Contact email is required";
        } else if (!emailPattern.test(item.email)) {
            error.email = "Invalid email format";
        }
        errorsFactoryEmployee.value.push(error);
    })
    return errorsFactoryEmployee.value.every(e => Object.keys(e).length === 0);
}
const validate = () => {
    let valid = true;
    const employeeValidation = setValidateEmployees();
    if (!employeeValidation) valid = false
    return valid;
}
const submit = async () => {
    if (!validate()) {
        $toast('Please fill all required fields', 'error')
        return
    }

    const payload = {
        factory_employees: factoryEmployee.value.map(item => ({
            ...(item.id ? { id: item.id } : {}),
            name: item.name,
            designation: item.designation,
            email: item.email,
        }))
    }

    try {
        const res = await proxy.store(route('web.client.employee.store'), payload);
        if (res === true) await getFactoryEmployee();
    } catch (error) {
        console.error('error submitting query: ', error)
        $toast('Submission failed','error')
    }
}
const getFactoryEmployee = async () => {
    try {
        const { data: res } = await axios.get(route('web.client.getEmployee'));
        if (Array.isArray(res) && res.length > 0) {
            factoryEmployee.value = res;
            totalEmployee.value = res.length;
        } else {
            // IMPORTANT PART
            factoryEmployee.value = [emptyRow()];
            totalEmployee.value = 0;
        }
    } catch (error) {
        console.error('Error fetching query importance:', error);
        factoryEmployee.value = [emptyRow()];
        totalEmployee.value = 0;
    }
}

onBeforeMount(async () => {
    await getFactoryEmployee();
})

</script>

<style scoped>
/* High-end Dashboard Aesthetics */
.x-small { font-size: 0.75rem; letter-spacing: 0.05rem; }

.custom-table thead {
    border-bottom: 2px solid #f1f4f9;
}

.employee-row:hover {
    background-color: #fbfcfe !important;
}

/* Make inputs look like flat text until focused */
:deep(.form-control) {
    background-color: transparent !important;
    border: 1px solid transparent !important;
    padding: 0.5rem 0.75rem;
    transition: all 0.2s ease;
    font-size: 0.9rem;
}

:deep(.form-control:focus) {
    background-color: #fff !important;
    border-color: #0d6efd !important;
    box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.1) !important;
}

.btn-action {
    width: 32px;
    height: 32px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    transition: transform 0.1s;
}

.btn-action:active {
    transform: scale(0.9);
}

.mt-30 { margin-top: 30px; }

/* Custom Badge background */
.bg-soft-primary { background-color: #e7f1ff; color: #0d6efd; }
</style>

